import { sseFailureCopy } from '../lib/sseFailure';
import { useLiveStatus } from './liveStatusContext';

// Persistent app-wide banner for a HARD-failed live channel — "the SPA can't talk to the
// backend at all". Renders nothing in the healthy case, and nothing for a transient drop
// (LiveStatusProvider's toast owns that, and this bar would be far too heavy for a blip).
// A1EE-364.
//
// Persistent on purpose: unlike the toast this describes a condition that does NOT clear
// itself, so a TTL would be wrong — it disappears when `failure` clears, i.e. when the
// channel actually reopens.
//
// Colour note, given how the toast got this wrong (A1EE-692): the fill here is only half the
// legibility story. This is a full-bleed bar with a bottom border between the header and the
// content, so its boundary does not depend on fill lightness the way a floating panel's does.
// What matters is the TEXT, and dark `-900` on a `-100` fill clears WCAG AA comfortably in
// both palettes. Do not "improve" this to a `-50` fill: that is `slate-50` to within a
// couple of percent of lightness, which is exactly the mistake the toast shipped.
const TONE = {
  // Something is structurally wrong and waiting will not fix it.
  reload: 'border-red-300 bg-red-100 text-red-900',
  // Transient upstream unhappiness, or offline. The wrapper is still retrying.
  wait: 'border-amber-300 bg-amber-100 text-amber-900',
  // A permissions answer, not an outage.
  access: 'border-slate-300 bg-slate-100 text-slate-800',
} as const;

export interface LiveStatusBannerProps {
  /** Injectable for tests; defaults to a real page reload. */
  onReload?: () => void;
}

export function LiveStatusBanner({ onReload }: LiveStatusBannerProps = {}) {
  const { failure } = useLiveStatus();
  if (!failure) return null;

  const { message, advice } = sseFailureCopy(failure.status);

  return (
    // `role="alert"` (assertive), not the toast's polite `role="status"`: this is an
    // app-level condition the user needs to know about now, not a passing confirmation.
    <div
      role="alert"
      className={
        'flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-6 py-2 text-sm font-medium ' +
        TONE[advice]
      }
    >
      <span aria-hidden>⚠</span>
      <span className="min-w-0 flex-1">{message}</span>
      {advice === 'reload' && (
        <button
          type="button"
          onClick={onReload ?? (() => window.location.reload())}
          className="shrink-0 rounded border border-red-400 bg-white px-2 py-1 text-xs font-semibold text-red-900 hover:border-red-600"
        >
          Reload page
        </button>
      )}
    </div>
  );
}
